import menuService from '@/services/master-data/menu.service';

/**
 * Menu Module
 *
 * State untuk navbar/sidebar yang di-fetch dari API dan dipakai oleh
 * semua layout (vertical, horizontal, twocolumn).
 *
 * Alur fetch:
 *   1. `fetchMenus` dipanggil sekali saat user login / app di-mount.
 *   2. `list()` mengembalikan parent menu + `children_count`.
 *   3. Untuk setiap parent dengan `children_count > 0`, ambil
 *      children via `detail(menu_id)` secara paralel.
 *   4. Simpan hasil gabungan sebagai tree di `state.items`.
 *
 * Struktur hasil (state.items[i]):
 *   {
 *     menu_id, title, url, route, status, parent_id,
 *     sequence, children_count,
 *     children: [ { menu_id, title, url, route, status, parent_id, ... } ]
 *   }
 */

export const state = {
  items: [],          // tree menu final
  loading: false,     // true selama fetch berjalan
  loaded: false,      // true setelah fetch pertama berhasil (untuk hindari re-fetch)
  error: null,        // pesan error fetch terakhir
};

export const getters = {
  /** Daftar menu aktif (status == 1), sudah sorted by sequence di server. */
  activeMenus(state) {
    return (state.items || []).filter((m) => Number(m.status) === 1);
  },

  /** Semua menu termasuk yang inactive (untuk halaman master data). */
  allMenus(state) {
    return state.items || [];
  },

  isLoading(state) {
    return state.loading;
  },

  isLoaded(state) {
    return state.loaded;
  },
};

export const mutations = {
  setLoading(state, value) {
    state.loading = !!value;
  },
  setItems(state, items) {
    state.items = Array.isArray(items) ? items : [];
    state.loaded = true;
    state.error = null;
  },
  setError(state, message) {
    state.error = message || null;
  },
  reset(state) {
    state.items = [];
    state.loading = false;
    state.loaded = false;
    state.error = null;
  },
};

/**
 * Normalisasi node menu dari response API jadi shape yang dipakai UI.
 * Recursive supaya children dari children juga ke-handle (kalau backend
 * suatu saat punya nested > 2 level).
 */
function normalizeNode(raw, parentId = '') {
  const node = {
    menu_id: raw?.menu_id ?? raw?.id ?? '',
    title: raw?.title ?? raw?.name ?? '',
    url: raw?.url ?? '',
    route: raw?.route ?? '',
    icon: raw?.icon ?? '',
    status: raw?.status ?? 1,
    parent_id: raw?.parent_id ?? parentId,
    sequence: raw?.sequence ?? 0,
    children: [],
  };
  const rawChildren = Array.isArray(raw?.children) ? raw.children : [];
  node.children = rawChildren.map((c) => normalizeNode(c, node.menu_id));
  node.children_count = node.children.length;
  return node;
}

/**
 * Ekstrak array tree dari response yang bisa beragam bentuknya:
 *   - { data: [...] }
 *   - { data: { menu: [...] } }
 *   - { data: { data: [...] } }
 *   - [...]
 */
function extractMenuArray(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.data?.menu)) return data.data.menu;
  if (Array.isArray(data?.data?.data)) return data.data.data;
  if (Array.isArray(data?.menu)) return data.menu;
  return [];
}

export const actions = {
  /**
   * Fetch menu untuk user yang login dari endpoint /me/menu.
   * Endpoint ini sudah return tree lengkap (parent + children) yang sudah
   * di-filter sesuai role user, jadi kita tidak perlu fetch detail per parent.
   *
   * Untuk CRUD master data (halaman Hak Akses) tetap pakai service yang sama
   * tapi method yang berbeda (list, detail, store, update, dll).
   *
   * Penanganan error:
   *   - Auth failure (401/419/440 atau 403 dengan pesan token) -> di-swallow
   *     karena sudah ditangani interceptor di helpers/api.js (clear storage +
   *     redirect ke /login dengan banner "sesi berakhir"). Kalau tetap di-throw
   *     ke caller, jadi unhandled promise rejection dan webpack dev-server
   *     menampilkan error overlay merah saat session expired.
   *   - Error lain (5xx, network error) -> tetap di-throw supaya bisa
   *     di-debug oleh caller.
   *
   * @param {object} ctx
   * @param {object} [opts]
   * @param {boolean} [opts.force=false] - paksa fetch ulang meskipun sudah loaded
   */
  async fetchMenus({ commit, state }, opts = {}) {
    const { force = false } = opts;
    if (state.loading) return state.items;
    if (state.loaded && !force) return state.items;

    commit('setLoading', true);
    try {
      const res = await menuService.myMenu();
      const rawArray = extractMenuArray(res?.data);
      const tree = rawArray.map((n) => normalizeNode(n));

      commit('setItems', tree);
      return tree;
    } catch (err) {
      const msg =
        err?.response?.data?.message || err?.message || 'Gagal memuat menu.';
      commit('setError', msg);
      commit('setItems', []); // tetap set ke array kosong supaya UI tidak loading forever

      // Swallow auth failures: sudah di-handle interceptor (redirect ke /login).
      // Kalau di-throw, jadi unhandled rejection -> overlay error webpack.
      const status = err?.response?.status;
      if (status === 401 || status === 419 || status === 440 || status === 403) {
        return [];
      }

      // Error lain (5xx, network error, dll) tetap di-throw supaya bisa
      // ditangkap caller / muncul di console untuk debugging.
      throw err;
    } finally {
      commit('setLoading', false);
    }
  },

  /** Reset state saat logout. */
  clear({ commit }) {
    commit('reset');
  },
};